// What the Worklist and History show: the status tabs, the filters and the counters.

import { isDay } from "@axny/platform-contracts";
import { addDays } from "./days";
import type { Holidays } from "./days";
import type { Assembled, LineView } from "./assemble";
import { assemble } from "./assemble";
import type { RoutingWindow, WindowPt } from "./window";
import type { ExcelHistoryRow } from "./excel";
import type { LineSource, SourceFilter } from "./deposco";
import { inSource } from "./deposco";
import type { MyCustomerFilter } from "./assignments";
import { isMyLine } from "./assignments";
import { divisionNamer } from "./divisions";
import { customerNamer, customerSearchText, keepsCustomer, withCustomerNames } from "./customer-names";
import { excelLine, kindOf } from "./excel";
import { WINDOW_RANK, routingWindow } from "./window";
import type { PeView } from "./pe-order";
import { peView } from "./pe-order";

export type Mode = "worklist" | "history";
/**
 * The warehouses: the source view has only PE, HT and SM (v3), so the picker offers exactly these.
 * Adding one is this list and the view.
 */
export const WAREHOUSE_GROUPS = ["PE", "HT", "SM"] as const;
export type WarehouseGroup = (typeof WAREHOUSE_GROUPS)[number];
export const DEFAULT_WAREHOUSES: WarehouseGroup[] = ["PE", "HT"];

export const isWarehouse = (wh: unknown): wh is WarehouseGroup => (WAREHOUSE_GROUPS as readonly unknown[]).includes(wh);

/**
 * A saved picker choice, read again: a warehouse no longer offered ("Other", before v3) is
 * dropped, and a choice left empty by that is the default, never "every warehouse".
 */
export const savedWarehouses = (saved: readonly unknown[]): WarehouseGroup[] => {
  const kept = saved.filter(isWarehouse);
  return kept.length || !saved.length ? kept : [...DEFAULT_WAREHOUSES];
};

/** Nothing ticked = every warehouse; a line with none (an Excel History row) passes any choice. */
export const inWarehouses = (wh: string | null, groups: readonly WarehouseGroup[]) =>
  !groups.length || !wh || (groups as readonly string[]).includes(wh);

export const WORKLIST_TABS = [
  { id: "all", label: "All open", dot: null },
  { id: "review", label: "To review", dot: "bg-primary" },
  { id: "overdue", label: "Past deadline", dot: "bg-red-600" },
  { id: "now", label: "Route now", dot: "bg-orange-600" },
  { id: "open", label: "Window open", dot: "bg-amber-500" },
  { id: "notyet", label: "Not open yet", dot: "bg-slate-400" },
  { id: "routed", label: "Routed · in production", dot: "bg-emerald-600" },
  { id: "hold", label: "On hold", dot: null },
  // Not in spec §3.4: without them a line with no routing rule, or one whose pick tickets are
  // all gone, would be in no tab but All open.
  { id: "norule", label: "No routing rule", dot: "bg-slate-300" },
  { id: "gone", label: "Gone from A2000", dot: "bg-slate-700" },
  // A Deposco line whose SOs all left Deposco's open orders without being marked shipped here.
  { id: "dgone", label: "Gone from Deposco", dot: "bg-slate-700" },
  // Open pick tickets A2000 has given no WMS # yet: read-only rows of their own, in no other tab.
  { id: "nowms", label: "No WMS", dot: "bg-stone-400" },
  // A WMS holding several customers, divisions, warehouses, first ship or cancel dates: allowed. A view
  // across the state tabs, not a state.
  { id: "mixed", label: "Mixed", dot: "bg-violet-600" },
  // A field its carry-over sources disagreed on, still to choose ("2 values", `carry.ts`): a view
  // across the state tabs, like Mixed.
  { id: "twovalues", label: "2 values", dot: "bg-amber-500" },
  // Some or all of its pick tickets SHIPPED or BILLED in the WMS (`a2000.ts`): a flag across the
  // state tabs, like Mixed. Nothing moves by itself.
  { id: "a2000", label: "Shipped in A2000", dot: "bg-teal-600" },
  // Some or all of its SOs Ship Complete, or with a ship date, in Deposco (`deposco.ts`): the same flag.
  { id: "dshipped", label: "Shipped in Deposco", dot: "bg-teal-600" },
] as const;

/** The tabs of one source only: shown when the Source filter is All or that source. */
export const TAB_SOURCE: Partial<Record<TabId, LineSource>> = {
  nowms: "A2000",
  gone: "A2000",
  a2000: "A2000",
  dgone: "Deposco",
  dshipped: "Deposco",
};
/** Whether a tab applies under the Source filter. */
export const tabInSource = (tab: TabId, source: SourceFilter) => {
  const only = TAB_SOURCE[tab];
  return !only || inSource(only, source);
};

export const HISTORY_TABS = [
  { id: "all", label: "All finished lines", dot: null },
  { id: "shipped", label: "Shipped", dot: "bg-teal-600" },
  { id: "cancelled", label: "Cancelled", dot: "bg-red-600" },
  // Lines from before WMS grouping: their pick tickets are on WMS lines now, with their entries.
  { id: "regrouped", label: "Regrouped by WMS", dot: "bg-slate-500" },
] as const;

export type TabId = (typeof WORKLIST_TABS)[number]["id"] | (typeof HISTORY_TABS)[number]["id"];

export const WORKLIST_DATE_FIELDS = [
  { id: "ptDate", label: "RECEIVED DATE" },
  { id: "start", label: "FIRST SHIP DATE" },
  { id: "cancel", label: "CANCEL DATE" },
  { id: "routeBy", label: "Route by" },
  { id: "readyDate", label: "READY DATE" },
  { id: "schedShip", label: "SCHEDULED SHIP DATE" },
  { id: "appointmentDate", label: "Appointment date" },
] as const;

export const HISTORY_DATE_FIELDS = [
  { id: "ptDate", label: "RECEIVED DATE" },
  { id: "start", label: "FIRST SHIP DATE" },
  { id: "cancel", label: "CANCEL DATE" },
  { id: "routedOn", label: "Routed on" },
  { id: "finishedAt", label: "Finished on" },
] as const;

export type DateFieldId = (typeof WORKLIST_DATE_FIELDS)[number]["id"] | (typeof HISTORY_DATE_FIELDS)[number]["id"];

export interface DateRange {
  field: DateFieldId;
  /** Preset id: 2w, 30d, 60d, 90d, next7, next14, year, any, custom. */
  preset: string;
  from: string | null;
  to: string | null;
}

export function presetRange(preset: string, today: string): { from: string | null; to: string | null } {
  switch (preset) {
    case "2w":
      return { from: addDays(today, -14), to: today };
    case "30d":
      return { from: addDays(today, -30), to: today };
    case "60d":
      return { from: addDays(today, -60), to: today };
    case "90d":
      return { from: addDays(today, -90), to: today };
    case "next7":
      return { from: today, to: addDays(today, 7) };
    case "next14":
      return { from: today, to: addDays(today, 14) };
    case "year":
      return { from: `${today.slice(0, 4)}-01-01`, to: today };
    case "since2026":
      return { from: "2026-01-01", to: null };
    default:
      return { from: null, to: null };
  }
}

export const WORKLIST_PRESETS = [
  { id: "2w", label: "Last 2 weeks" },
  { id: "30d", label: "Last 30 days" },
  { id: "60d", label: "Last 60 days" },
  { id: "next7", label: "Next 7 days" },
  { id: "next14", label: "Next 14 days" },
  { id: "any", label: "Any date" },
] as const;

export const HISTORY_PRESETS = [
  { id: "90d", label: "Last 90 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "since2026", label: "Since Jan 1, 2026" },
  { id: "year", label: "This year" },
  { id: "any", label: "Any date" },
] as const;

/**
 * The Worklist: received in the last 2 weeks. History: finished in the last 90 days (spec §3.15) —
 * by finish date, as open pick tickets can be over a year old when they ship; the server sends only
 * those (`historySubset`).
 */
export const defaultRange = (mode: Mode, today: string): DateRange =>
  mode === "worklist"
    ? { field: "ptDate", preset: "2w", ...presetRange("2w", today) }
    : { field: "finishedAt", preset: "90d", ...presetRange("90d", today) };

/** A line with its routing window — computed once per render. */
export interface Row {
  line: LineView;
  window: RoutingWindow;
  /** PE Order Ship Date and PE Order Status as the cells show them: stored, else the prefill (`pe-order.ts`). */
  pe: PeView;
}

/** The pick tickets a line's window counts: the open ones (all of them on a line gone from A2000). */
export const windowPts = (line: Pick<LineView, "pts">): WindowPt[] => {
  const live = line.pts.filter((p) => !p.gone);
  return (live.length ? live : line.pts).map((p) => ({
    pickTicket: p.pickTicket,
    customer: p.customerName,
    start: p.start,
    cancel: p.cancel,
    openBdays: p.openBdays,
    dueBdays: p.dueBdays,
    ...(p.rule ? { rule: p.rule } : {}),
  }));
};

export const toRows = (lines: readonly LineView[], today: string, holidays: Holidays): Row[] =>
  lines.map((line) => ({
    line,
    window: routingWindow({ pts: windowPts(line), routingCategory: line.routingCategory }, today, holidays),
    pe: peView(line, holidays),
  }));

/**
 * Every row of both pages: the lines `assemble` makes and History's workbook rows, side by side —
 * never merged. The pages and the Assistant's reports (`backend/src/reports.ts`) read this one, with
 * the same holidays (`/api/routing/holidays`), so their windows agree.
 */
export function routingRows(
  input: Parameters<typeof assemble>[0] & { excel?: readonly ExcelHistoryRow[] | null },
  today: string,
  holidays: Holidays,
): { assembled: Assembled; rows: Row[] } {
  const assembled = assemble({ today, ...input });
  const abbrev = divisionNamer(input.divisionAbbrevs);
  const excel = (input.excel ?? []).map((r) => excelLine(r, abbrev));
  withCustomerNames(excel, customerNamer(input.customerNames));
  return { assembled, rows: toRows([...assembled.lines, ...excel], today, holidays) };
}

export function dateOf(row: Row, field: DateFieldId): string | null {
  const l = row.line;
  const v =
    field === "ptDate"
      ? l.ptDate
      : field === "start"
        ? l.start
        : field === "cancel"
          ? l.cancel
          : field === "routeBy"
            ? row.window.routeBy
            : field === "finishedAt"
              ? (l.finished?.at ?? null)
              : l.fields[field];
  return isDay(v) ? v : null;
}

/**
 * The date filter keeps the line: no range, or its date in it (no date: not kept). RECEIVED DATE: any
 * of its pick tickets' — a WMS line holds pick tickets received on different days.
 */
export function inDateRange(row: Row, range: DateRange): boolean {
  if (!range.from && !range.to) return true;
  if (range.field === "ptDate" && row.line.ptDates.length)
    return row.line.ptDates.some((d) => isDay(d) && (!range.from || d >= range.from) && (!range.to || d <= range.to));
  const d = dateOf(row, range.field);
  return !!d && (!range.from || d >= range.from) && (!range.to || d <= range.to);
}

export function inTab(row: Row, tab: TabId): boolean {
  const s = row.window.state;
  const l = row.line;
  switch (tab) {
    case "nowms":
      return l.noWms;
    case "all":
      return !l.noWms;
    case "review":
      return l.review === "new" || l.review === "updated" || l.review === "split";
    case "overdue":
      return !l.noWms && !l.allGone && s === "overdue";
    case "now":
      return !l.noWms && !l.allGone && (s === "today" || s === "tomorrow");
    case "open":
      return !l.noWms && !l.allGone && (s === "due" || s === "open" || s === "requested");
    case "notyet":
      return !l.noWms && !l.allGone && s === "notyet";
    case "routed":
      return !l.noWms && !l.allGone && s === "routed";
    case "hold":
      return !l.noWms && !l.allGone && s === "hold";
    case "norule":
      return !l.noWms && !l.allGone && s === "norule";
    case "gone":
      return !l.noWms && l.allGone && l.source === "A2000";
    case "dgone":
      return l.allGone && l.source === "Deposco";
    case "dshipped":
      return !!l.deposco?.shipped;
    case "mixed":
      return !l.noWms && l.mixed !== null;
    case "twovalues":
      return !l.noWms && hasTwoValues(l);
    case "a2000":
      return !l.noWms && l.a2000 !== null;
    case "shipped":
      return l.finished?.status === "Shipped";
    case "cancelled":
      return l.finished?.status === "Cancelled";
    case "regrouped":
      return l.finished?.status === "Regrouped by WMS";
  }
}

/** A line with a "2 values" choice open: a field its carry-over sources disagreed on. */
export const hasTwoValues = (l: Pick<LineView, "carry">) =>
  Object.values(l.carry.conflicts).some((values) => !!values?.length);

/** The Worklist tabs a line is in by its state — every tab but All open, To review, Mixed, 2 values and Shipped in A2000. */
export const STATE_TABS = WORKLIST_TABS.filter(
  (t) =>
    t.id !== "all" &&
    t.id !== "review" &&
    t.id !== "mixed" &&
    t.id !== "twovalues" &&
    t.id !== "a2000" &&
    t.id !== "dshipped",
);

/** The one state tab an open line is in (`inTab`'s own test), as its label: "Past deadline". */
export function stateTabOf(row: Row): (typeof STATE_TABS)[number]["label"] {
  const tab = STATE_TABS.find((t) => inTab(row, t.id));
  if (!tab) throw new Error(`line ${row.line.key} is in no Worklist tab`);
  return tab.label;
}

export const STATE_TAB_LABELS = STATE_TABS.map((t) => t.label);

/** "N to route now or late" (the Worklist's summary, and Ask AI's "route today"): Past deadline + Route now. */
export const ROUTE_TODAY_TABS = ["overdue", "now"] as const satisfies readonly TabId[];
export const routeTodayOrLate = (row: Row) => ROUTE_TODAY_TABS.some((t) => inTab(row, t));

export interface Filters {
  /** A2000, Deposco, or both ("all"). */
  source: SourceFilter;
  warehouses: WarehouseGroup[];
  customers: string[];
  statuses: string[];
  range: DateRange;
  search: string;
  /** Header "Show values" filters: column id → the values kept. */
  columns: Record<string, string[]>;
  /** Order / Sample / Transfer kept (History); empty = all. */
  kinds: string[];
  /** Whether the workbook's duplicate rows show (History). */
  duplicates: boolean;
  /** My customers: the lines holding one of them (`isMyLine`); none, every line. */
  mine?: MyCustomerFilter;
}

export type ColumnValue = (row: Row, column: string) => string | readonly string[];

/** The lines of a page before the tab: its mode, the warehouse and every filter. */
export function pageRows(
  rows: readonly Row[],
  mode: Mode,
  f: Filters,
  /** A column's value, or its several (a Mixed line's divisions, Work Needed): kept if ANY is. */
  columnValue: ColumnValue,
): Row[] {
  const q = f.search.trim().toLowerCase();
  return rows.filter((row) => {
    const l = row.line;
    if (mode === "worklist" ? l.finished !== null : l.finished === null) return false;
    if (!inSource(l.source, f.source)) return false;
    // A Mixed line counts in every warehouse and under every customer it holds.
    if (!(l.whs.length ? l.whs : [l.wh]).some((wh) => inWarehouses(wh, f.warehouses))) return false;
    if (f.kinds.length && !f.kinds.includes(kindOf(l))) return false;
    if (!f.duplicates && l.excel?.duplicate) return false;
    // By shown name (customers sharing one are one option) or by original name (Ask AI, a saved view).
    if (!keepsCustomer(l, f.customers)) return false;
    if (f.mine && !isMyLine(l, f.mine)) return false;
    if (f.statuses.length && !f.statuses.includes(l.fields.routingStatus)) return false;
    if (!inDateRange(row, f.range)) return false;
    for (const [col, kept] of Object.entries(f.columns)) {
      if (!kept.length) continue;
      const v = columnValue(row, col);
      if (!(typeof v === "string" ? [v] : v).some((one) => kept.includes(one))) return false;
    }
    if (q) {
      const hay = [
        // A customer by its shown name, its original name or its code; a division by its abbreviation or its name.
        customerSearchText(l),
        l.divisions.join(" "),
        l.divs.map((d) => d.abbrev).join(" "),
        l.wmsNo,
        l.pos.join(" "),
        l.orderNos.join(" "),
        l.wms,
        l.pts.map((p) => p.pickTicket).join(" "),
        l.fields.routingRef,
        l.excel?.poPt ?? "",
        // A Deposco line by its DLIX, CO, customer order, group or DC (its SO #s are its pick tickets).
        l.dcs.join(" "),
        ...(l.deposco
          ? [l.deposco.dlixes, l.deposco.cos, l.deposco.customerOrders, l.deposco.groups].map((v) => v.join(" "))
          : []),
      ]
        .join(" ")
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

/**
 * A line's place among lines its order finds equal, and in the grid's kept order (`stable-order.ts`):
 * its group key (`WMS|…`, `DEP|…`), which it keeps across its first save — its key goes from
 * `auto:WMS|…` to the stored id. A row with no group key (No WMS, a workbook row) by its own key.
 */
export const lineOrderKey = (line: Pick<LineView, "groupKey" | "key">) => line.groupKey || line.key;

/** The last word between two lines an order finds equal: a refetch or a save never swaps them. */
export const lineTiebreak = (a: Row, b: Row) => lineOrderKey(a.line).localeCompare(lineOrderKey(b.line));

/** The Worklist's default order: most urgent first, then Route by, then customer, then `lineTiebreak`. */
export function priorityOrder(a: Row, b: Row): number {
  return (
    Number(a.line.allGone) - Number(b.line.allGone) ||
    WINDOW_RANK[a.window.state] - WINDOW_RANK[b.window.state] ||
    (a.window.routeBy ?? "9").localeCompare(b.window.routeBy ?? "9") ||
    a.line.customerName.localeCompare(b.line.customerName) ||
    lineTiebreak(a, b)
  );
}

/** History's default order: last finished first, then customer, then `lineTiebreak`. */
export const historyOrder = (a: Row, b: Row) =>
  (b.line.finished?.at ?? "").localeCompare(a.line.finished?.at ?? "") ||
  a.line.customerName.localeCompare(b.line.customerName) ||
  lineTiebreak(a, b);

/**
 * A header's "Show values" search: case-insensitive "contains", on what the list says (a code with
 * its name, an abbreviation with the full name) or the value itself.
 */
export const headerValueMatches = (v: { value: string; label?: string }, search: string) =>
  !search.trim() || `${v.label ?? ""} ${v.value}`.toLowerCase().includes(search.trim().toLowerCase());

/**
 * A column's sort: numbers as numbers, text in reading order with its numbers counted ("PO 2"
 * before "PO 10"), digits before letters ("10SPOT" before "ZAZZLE"). Z → A is exactly this reversed.
 */
export function compareSortValues(a: unknown, b: unknown): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a ?? "").localeCompare(String(b ?? ""), "en-US", { numeric: true, sensitivity: "base" });
}
